import pick from 'lodash/pick';
import {
  checkValidationOfPromoCode,
  confirmPaymentBackend,
  initiatePrivileged,
  transitionPrivileged,
} from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import {
  fetchCurrentUserHasOrdersSuccess,
  fetchCurrentUser,
  saveShippingAddressInProfile,
} from '../../ducks/user.duck';
import { generateRandomNumber } from '../../util/types';
import {
  PRIVACY_POLICY_ASSET_NAME,
  TOS_ASSET_NAME,
} from '../AuthenticationPage/AuthenticationPage.duck';
import { fetchPageAssets } from '../../ducks/hostedAssets.duck';

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/CheckoutPage/SET_INITIAL_VALUES';

export const INITIATE_ORDER_REQUEST = 'app/CheckoutPage/INITIATE_ORDER_REQUEST';
export const INITIATE_ORDER_SUCCESS = 'app/CheckoutPage/INITIATE_ORDER_SUCCESS';
export const INITIATE_ORDER_ERROR = 'app/CheckoutPage/INITIATE_ORDER_ERROR';

export const CONFIRM_PAYMENT_REQUEST = 'app/CheckoutPage/CONFIRM_PAYMENT_REQUEST';
export const CONFIRM_PAYMENT_SUCCESS = 'app/CheckoutPage/CONFIRM_PAYMENT_SUCCESS';
export const CONFIRM_PAYMENT_ERROR = 'app/CheckoutPage/CONFIRM_PAYMENT_ERROR';

export const SPECULATE_TRANSACTION_REQUEST = 'app/CheckoutPage/SPECULATE_TRANSACTION_REQUEST';
export const SPECULATE_TRANSACTION_SUCCESS = 'app/CheckoutPage/SPECULATE_TRANSACTION_SUCCESS';
export const SPECULATE_TRANSACTION_ERROR = 'app/CheckoutPage/SPECULATE_TRANSACTION_ERROR';

export const STRIPE_CUSTOMER_REQUEST = 'app/CheckoutPage/STRIPE_CUSTOMER_REQUEST';
export const STRIPE_CUSTOMER_SUCCESS = 'app/CheckoutPage/STRIPE_CUSTOMER_SUCCESS';
export const STRIPE_CUSTOMER_ERROR = 'app/CheckoutPage/STRIPE_CUSTOMER_ERROR';

export const INITIATE_INQUIRY_REQUEST = 'app/CheckoutPage/INITIATE_INQUIRY_REQUEST';
export const INITIATE_INQUIRY_SUCCESS = 'app/CheckoutPage/INITIATE_INQUIRY_SUCCESS';
export const INITIATE_INQUIRY_ERROR = 'app/CheckoutPage/INITIATE_INQUIRY_ERROR';

export const HANDLE_SHIPPING_TYPE = 'app/CheckoutPage/HANDLE_SHIPPING_TYPE';
export const HANDLE_SHIPPING_TYPE_OFF = 'app/CheckoutPage/HANDLE_SHIPPING_TYPE_OFF';

export const HANDLE_STRIPE_CHECKOUT = 'app/CheckoutPage/HANDLE_STRIPE_CHECKOUT';

export const INVALID_PROMOCODE = 'app/CheckoutPage/INVALID_PROMOCODE';

export const PROMOCODE_APPLIED = 'app/CheckoutPage/PROMOCODE_APPLIED';

export const APPLY_PROMOCODE = 'app/CheckoutPage/APPLY_PROMOCODE';

export const SET_USER_HAVE_FIRST_TRANSACTION = 'app/SET_USER_HAVE_FIRST_TRANSACTION';

// ================ Reducer ================ //

const initialState = {
  listing: null,
  orderData: null,
  speculateTransactionInProgress: false,
  speculateTransactionError: null,
  speculatedTransaction: null,
  transaction: null,
  initiateOrderError: null,
  confirmPaymentError: null,
  stripeCustomerFetched: false,
  initiateInquiryInProgress: false,
  initiateInquiryError: null,
  shippingTypeInProgress: false,
  stripeCheckoutSecret: null,
  invalidPromoCode: false,
  ispromoCodeApplied: false,
  applyPromoCodeInProgress: false,
  userHasFirstTransaction: true,
};

export default function checkoutPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case APPLY_PROMOCODE:
      return { ...state, applyPromoCodeInProgress: payload };

    case SET_USER_HAVE_FIRST_TRANSACTION:
      return { ...state, userHasFirstTransaction: payload };

    case SPECULATE_TRANSACTION_REQUEST:
      return {
        ...state,
        speculateTransactionInProgress: true,
        speculateTransactionError: null,
        speculatedTransaction: null,
        stripeCheckoutSecret: null,
      };
    case PROMOCODE_APPLIED:
      return { ...state, ispromoCodeApplied: payload };

    case SPECULATE_TRANSACTION_SUCCESS:
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculatedTransaction: payload.transaction,
      };
    case SPECULATE_TRANSACTION_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculateTransactionError: payload,
      };

    case INITIATE_ORDER_REQUEST:
      return { ...state, initiateOrderError: null, stripeCheckoutSecret: null };
    case INITIATE_ORDER_SUCCESS:
      return { ...state, transaction: payload };
    case INITIATE_ORDER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, initiateOrderError: payload };

    case CONFIRM_PAYMENT_REQUEST:
      return { ...state, confirmPaymentError: null };
    case CONFIRM_PAYMENT_SUCCESS:
      return state;
    case CONFIRM_PAYMENT_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, confirmPaymentError: payload };

    case STRIPE_CUSTOMER_REQUEST:
      return { ...state, stripeCustomerFetched: false };
    case STRIPE_CUSTOMER_SUCCESS:
      return { ...state, stripeCustomerFetched: true };
    case STRIPE_CUSTOMER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, stripeCustomerFetchError: payload };

    case INITIATE_INQUIRY_REQUEST:
      return { ...state, initiateInquiryInProgress: true, initiateInquiryError: null };
    case INITIATE_INQUIRY_SUCCESS:
      return { ...state, initiateInquiryInProgress: false };
    case INITIATE_INQUIRY_ERROR:
      return { ...state, initiateInquiryInProgress: false, initiateInquiryError: payload };
    case HANDLE_SHIPPING_TYPE:
      return { ...state, shippingTypeInProgress: true };
    case HANDLE_SHIPPING_TYPE_OFF:
      return { ...state, shippingTypeInProgress: false };

    case HANDLE_STRIPE_CHECKOUT:
      return { ...state, stripeCheckoutSecret: payload };
    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const initiateOrderRequest = () => ({ type: INITIATE_ORDER_REQUEST });

const initiateOrderSuccess = order => ({
  type: INITIATE_ORDER_SUCCESS,
  payload: order,
});

const initiateOrderError = e => ({
  type: INITIATE_ORDER_ERROR,
  error: true,
  payload: e,
});

const confirmPaymentRequest = () => ({ type: CONFIRM_PAYMENT_REQUEST });

const confirmPaymentSuccess = orderId => ({
  type: CONFIRM_PAYMENT_SUCCESS,
  payload: orderId,
});

const confirmPaymentError = e => ({
  type: CONFIRM_PAYMENT_ERROR,
  error: true,
  payload: e,
});

export const speculateTransactionRequest = () => ({ type: SPECULATE_TRANSACTION_REQUEST });

export const speculateTransactionSuccess = transaction => ({
  type: SPECULATE_TRANSACTION_SUCCESS,
  payload: { transaction },
});

export const speculateTransactionError = e => ({
  type: SPECULATE_TRANSACTION_ERROR,
  error: true,
  payload: e,
});

export const stripeCustomerRequest = () => ({ type: STRIPE_CUSTOMER_REQUEST });
export const stripeCustomerSuccess = () => ({ type: STRIPE_CUSTOMER_SUCCESS });
export const stripeCustomerError = e => ({
  type: STRIPE_CUSTOMER_ERROR,
  error: true,
  payload: e,
});

export const initiateInquiryRequest = () => ({ type: INITIATE_INQUIRY_REQUEST });
export const initiateInquirySuccess = () => ({ type: INITIATE_INQUIRY_SUCCESS });
export const initiateInquiryError = e => ({
  type: INITIATE_INQUIRY_ERROR,
  error: true,
  payload: e,
});

export const invalidPromoCode = e => ({ type: INVALID_PROMOCODE, payload: e });

/* ================ Thunks ================ */

export const initiateOrder = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition,
  billingDetails,
  promoCode = false
) => (dispatch, getState, sdk) => {
  dispatch(initiateOrderRequest());
  const shippingType = orderParams?.shippingType;
  if (!shippingType) {
    return null;
  }
  const isPopNouvelleUser = orderParams?.isPopNouvelleUser;
  const isCreatedByUsingScript = orderParams?.isCreatedByUsingScript;

  const shippingData = orderParams?.shippingData;
  const viber = orderParams?.viber;
  const currentUser = getState().user.currentUser;
  const { attributes } = currentUser || {};
  const { profile } = attributes || {};
  const { publicData } = profile || {};
  const { shippingAddress = [] } = publicData || {};
  let newAddresses;
  if (shippingData && shippingData?.isNew) {
    newAddresses = [...shippingAddress, shippingData];
  } else if (shippingData) {
    const newAddress = [...shippingAddress];
    newAddress[(shippingData?.index)] = shippingData;
    newAddresses = newAddress;
  }

  // If we already have a transaction ID, we should transition, not
  // initiate.
  const isTransition = !!transactionId;

  const { deliveryMethod, quantity, bookingDates, ...otherOrderParams } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  // Parameters only for client app's server
  const recipientCountry = orderParams?.protectedData?.shippingDetails?.address?.country || orderParams?.recipientCountry || 'GR';
  const orderData = deliveryMethod ? { deliveryMethod, recipientCountry } : { recipientCountry };

  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
  };

  const bodyParams = isTransition
    ? {
      id: transactionId,
      transition: transitionName,
      params: transitionParams,
    }
    : {
      processAlias: isPopNouvelleUser
        ? 'from-popnouvelle-booking-process/release-1'
        : shippingType == 'direct'
          ? processAlias
          : 'advance-default-purchase/release-1',
      transition: transitionName,
      params: transitionParams,
    };
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSucces = response => {
    console.log('donn ss---', response);
    const clientSecret = response?.clientSecret;
    const transactionId = response?.txId;

    if (window !== undefined && clientSecret) {
      const allIds = JSON.parse(localStorage.getItem('transactionIds')) || [];
      if (!allIds.includes(transactionId)) {
        allIds.push(transactionId);
        localStorage.setItem('transactionIds', JSON.stringify(allIds));
      }
    }

    if (clientSecret) {
      dispatch({ type: HANDLE_STRIPE_CHECKOUT, payload: clientSecret });
    }

    return response;
    // aarsh
    // const url = response?.url;
    // if (url) {
    //   window.location.href = url;
    // }
    // const entities = denormalisedResponseEntities(response);
    // const order = entities[0];
    // dispatch(initiateOrderSuccess(order));
    // dispatch(fetchCurrentUserHasOrdersSuccess(true));
    // return order;
  };

  const handleError = e => {
    dispatch(initiateOrderError(storableError(e)));
    const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
    log.error(e, 'initiate-order-failed', {
      ...transactionIdMaybe,
      listingId: orderParams.listingId.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
    });
    throw e;
  };

  if (isTransition && isPrivilegedTransition) {
    console.log('donnnn---1');
    // transition privileged
    return transitionPrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSucces)
      .catch(handleError);
  } else if (isTransition) {
    console.log('donnnn---2');
    // transition non-privileged
    return sdk.transactions
      .transition(bodyParams, queryParams)
      .then(handleSucces)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    console.log('donnnn---3');
    // return;
    // initiate privileged
    return initiatePrivileged({
      isSpeculative: false,
      orderData,
      bodyParams,
      queryParams,
      billingDetails,
      promoCode,
    })
      .then(handleSucces)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiate(bodyParams, queryParams)
      .then(handleSucces)
      .catch(handleError);
  }
};

export const confirmPayment = (transactionId, transitionName, transitionParams = {}) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(confirmPaymentRequest());
  const bodyParams = {
    id: transactionId,
    transition: transitionName,
    params: {
      protectedData: {
        ...transitionParams,
        orderId: generateRandomNumber(),
      },
    },
  };
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };
  return (
    confirmPaymentBackend({ bodyParams, queryParams })
      // return sdk.transactions
      //   .transition(bodyParams, queryParams)
      .then(response => {
        console.log('confirmPayment------', confirmPayment);
        const order = response.data.data;
        dispatch(confirmPaymentSuccess(order.id));
        return order;
      })
      .catch(e => {
        dispatch(confirmPaymentError(storableError(e)));
        const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
        log.error(e, 'initiate-order-failed', {
          ...transactionIdMaybe,
        });
        throw e;
      })
  );
};

export const sendMessage = params => (dispatch, getState, sdk) => {
  const message = params.message;
  const orderId = params.id;

  if (message) {
    return sdk.messages
      .send({ transactionId: orderId, content: message })
      .then(() => {
        return { orderId, messageSuccess: true };
      })
      .catch(e => {
        log.error(e, 'initial-message-send-failed', { txId: orderId });
        return { orderId, messageSuccess: false };
      });
  } else {
    return Promise.resolve({ orderId, messageSuccess: true });
  }
};

/**
 * Initiate transaction against default-inquiry process
 * Note: At this point inquiry transition is made directly against Marketplace API.
 *       So, client app's server is not involved here unlike with transitions including payments.
 *
 * @param {*} inquiryParams contains listingId and protectedData
 * @param {*} processAlias 'default-inquiry/release-1'
 * @param {*} transitionName 'transition/inquire-without-payment'
 * @returns
 */
export const initiateInquiryWithoutPayment = (inquiryParams, processAlias, transitionName) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(initiateInquiryRequest());

  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: listing?.id?.uuid,
    });
    dispatch(initiateInquiryError(storableError(error)));
    return Promise.reject(error);
  }

  const bodyParams = {
    transition: transitionName,
    processAlias,
    params: inquiryParams,
  };
  const queryParams = {
    include: ['provider'],
    expand: true,
  };

  return sdk.transactions
    .initiate(bodyParams, queryParams)
    .then(response => {
      const transactionId = response.data.data.id;
      dispatch(initiateInquirySuccess());
      return transactionId;
    })
    .catch(e => {
      dispatch(initiateInquiryError(storableError(e)));
      throw e;
    });
};

/**
 * Initiate or transition the speculative transaction with the given
 * booking details
 *
 * The API allows us to do speculative transaction initiation and
 * transitions. This way we can create a test transaction and get the
 * actual pricing information as if the transaction had been started,
 * without affecting the actual data.
 *
 * We store this speculative transaction in the page store and use the
 * pricing info for the booking breakdown to get a proper estimate for
 * the price with the chosen information.
 */
export const speculateTransaction = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition,
  promoCode
) => (dispatch, getState, sdk) => {
  console.log('promo inside duck-----', promoCode);
  if (promoCode) {
    // dispatch(speculateTransactionRequest());
    dispatch({ type: APPLY_PROMOCODE, payload: true });

    // const pageAsset = {
    //   termsOfService: `content/pages/${TOS_ASSET_NAME}.json`,
    //   privacyPolicy: `content/pages/${PRIVACY_POLICY_ASSET_NAME}.json`,
    // };
    // dispatch(fetchPageAssets(pageAsset, true));
    // If we already have a transaction ID, we should transition, not
    // initiate.
    const isTransition = !!transactionId;

    const { deliveryMethod, quantity, bookingDates, ...otherOrderParams } = orderParams;
    const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
    const bookingParamsMaybe = bookingDates || {};

    // Parameters only for client app's server
    const orderData = deliveryMethod ? { deliveryMethod } : {};

    // Parameters for Marketplace API
    const transitionParams = {
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...otherOrderParams,
      cardToken: 'CheckoutPage_speculative_card_token',
    };

    const bodyParams = isTransition
      ? {
        id: transactionId,
        transition: transitionName,
        params: transitionParams,
      }
      : {
        processAlias: 'advance-default-purchase/release-1',
        transition: transitionName,
        params: transitionParams,
      };

    const queryParams = {
      include: ['booking', 'provider'],
      expand: true,
    };

    const handleSuccess = response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the speculate response');
      }
      const tx = entities[0];
      dispatch(speculateTransactionSuccess(tx));

      dispatch({ type: APPLY_PROMOCODE, payload: false });
      dispatch({ type: PROMOCODE_APPLIED, payload: true });
    };

    const handleError = e => {
      console.log('here in speculateTransaction error---', e);
      log.error(e, 'speculate-transaction-failed', {
        listingId: transitionParams.listingId.uuid,
        ...quantityMaybe,
        ...bookingParamsMaybe,
        ...orderData,
      });
      dispatch({ type: APPLY_PROMOCODE, payload: false });
      dispatch({ type: PROMOCODE_APPLIED, payload: false });

      return dispatch(speculateTransactionError(storableError(e)));
    };

    if (isTransition && isPrivilegedTransition) {
      // transition privileged
      return transitionPrivileged({
        isSpeculative: true,
        orderData,
        bodyParams,
        queryParams,
        promoCode: promoCode,
      })
        .then(handleSuccess)
        .catch(handleError);
    } else if (isTransition) {
      // transition non-privileged
      return sdk.transactions
        .transitionSpeculative(bodyParams, queryParams, promoCode)
        .then(handleSuccess)
        .catch(handleError);
    } else if (isPrivilegedTransition) {
      // initiate privileged
      return initiatePrivileged({
        isSpeculative: true,
        orderData,
        bodyParams,
        queryParams,
        promoCode: promoCode,
      })
        .then(handleSuccess)
        .catch(handleError);
    } else {
      // initiate non-privileged
      return sdk.transactions
        .initiateSpeculative(bodyParams, queryParams)
        .then(handleSuccess)
        .catch(handleError);
    }
  } else {
    console.log('here in speculateTransac');
    dispatch(speculateTransactionRequest());
    const pageAsset = {
      termsOfService: `content/pages/${TOS_ASSET_NAME}.json`,
      privacyPolicy: `content/pages/${PRIVACY_POLICY_ASSET_NAME}.json`,
    };
    dispatch(fetchPageAssets(pageAsset, true));
    // If we already have a transaction ID, we should transition, not
    // initiate.
    const isTransition = !!transactionId;

    const { deliveryMethod, quantity, bookingDates, ...otherOrderParams } = orderParams;
    const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
    const bookingParamsMaybe = bookingDates || {};

    // Parameters only for client app's server
    const orderData = deliveryMethod ? { deliveryMethod } : {};

    // Parameters for Marketplace API
    const transitionParams = {
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...otherOrderParams,
      cardToken: 'CheckoutPage_speculative_card_token',
    };

    const bodyParams = isTransition
      ? {
        id: transactionId,
        transition: transitionName,
        params: transitionParams,
      }
      : {
        processAlias: 'advance-default-purchase/release-1',
        transition: transitionName,
        params: transitionParams,
      };

    const queryParams = {
      include: ['booking', 'provider'],
      expand: true,
    };

    const handleSuccess = response => {
      const { attributes } = response?.data?.data || {};
      const { lastTransition, payinTotal } = attributes || {};
      // if (lastTransition == 'transition/request-payment') {
      //   if (typeof window.gtag === 'function') {
      //     gtag('event', 'conversion', {
      //       send_to: process.env.REACT_APP_CHECKOUT_ID,
      //       // "AW-16647714105/RQm6CNXN-8MZELnqn4I-",
      //       value: payinTotal?.amount/100 || 1,
      //       currency: 'EUR',
      //     });
      //   }

      // }

      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the speculate response');
      }
      const tx = entities[0];
      dispatch(speculateTransactionSuccess(tx));
    };

    const handleError = e => {
      log.error(e, 'speculate-transaction-failed', {
        listingId: transitionParams.listingId.uuid,
        ...quantityMaybe,
        ...bookingParamsMaybe,
        ...orderData,
      });
      return dispatch(speculateTransactionError(storableError(e)));
    };

    if (isTransition && isPrivilegedTransition) {
      console.log('initiate prev first1');
      // transition privileged
      return transitionPrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
        .then(handleSuccess)
        .catch(handleError);
    } else if (isTransition) {
      console.log('initiate prev first2');
      // transition non-privileged
      return sdk.transactions
        .transitionSpeculative(bodyParams, queryParams)
        .then(handleSuccess)
        .catch(handleError);
    } else if (isPrivilegedTransition) {
      console.log('initiate prev first3');
      // initiate privileged
      return initiatePrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
        .then(handleSuccess)
        .catch(handleError);
    } else {
      // initiate non-privileged
      return sdk.transactions
        .initiateSpeculative(bodyParams, queryParams)
        .then(handleSuccess)
        .catch(handleError);
    }
  }
};
export const speculateTransactionShipping = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition,
  promoCode = false
) => (dispatch, getState, sdk) => {
  // dispatch(speculateTransactionRequest());
  dispatch({ type: HANDLE_SHIPPING_TYPE });
  // If we already have a transaction ID, we should transition, not
  // initiate.
  const isTransition = !!transactionId;

  const { deliveryMethod, quantity, bookingDates, ...otherOrderParams } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  // Parameters only for client app's server
  const recipientCountry = orderParams?.recipientCountry || 'GR';
  const orderData = deliveryMethod ? { deliveryMethod, recipientCountry } : { recipientCountry };

  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
    cardToken: 'CheckoutPage_speculative_card_token',
  };

  const bodyParams = isTransition
    ? {
      id: transactionId,
      transition: transitionName,
      params: transitionParams,
    }
    : {
      processAlias: 'advance-default-purchase/release-1',
      transition: transitionName,
      params: transitionParams,
    };

  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSuccess = response => {
    // dispatch({ type: PROMOCODE_APPLIED,payload:true });
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the speculate response');
    }
    const tx = entities[0];
    dispatch({ type: HANDLE_SHIPPING_TYPE_OFF });
    dispatch(speculateTransactionSuccess(tx));
    // dispatch({ type: PROMOCODE_APPLIED,payload:true });
  };

  const handleError = e => {
    log.error(e, 'speculate-transaction-failed', {
      listingId: transitionParams.listingId.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
    });
    dispatch({ type: HANDLE_SHIPPING_TYPE_OFF });
    return dispatch(speculateTransactionError(storableError(e)));
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transitionSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({
      isSpeculative: true,
      orderData,
      bodyParams,
      queryParams,
      promoCode,
    })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiateSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};

// StripeCustomer is a relantionship to currentUser
// We need to fetch currentUser with correct params to include relationship
export const stripeCustomer = () => (dispatch, getState, sdk) => {
  dispatch(stripeCustomerRequest());

  return dispatch(fetchCurrentUser({ include: ['stripeCustomer.defaultPaymentMethod'] }))
    .then(response => {
      dispatch(stripeCustomerSuccess());
    })
    .catch(e => {
      dispatch(stripeCustomerError(storableError(e)));
    });
};

export const verifyPromoCode = (promoCode, listingId) => (dispatch, getState, sdk) => {
  return checkValidationOfPromoCode({ promoCode, listingId });
};
export const UserHasAnyTransactionBefore = currentUser => (dispatch, getState, sdk) => {
  sdk.transactions
    .query({
      only: 'order',
    })
    .then(res => {
      console.log('hellofrom user inside duck---------', res?.data?.data?.length >= 1);

      if (res?.data?.data?.length >= 1) {
        dispatch({ type: SET_USER_HAVE_FIRST_TRANSACTION, payload: false });
      }
    })
    .catch(e => {
      dispatch({ type: SET_USER_HAVE_FIRST_TRANSACTION, payload: false });
      console.log('hello from error', e);
    });
  // return checkValidationOfPromoCode({promoCode})
};
